import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Statistic,
  message,
  Modal,
  Form,
  Input,
  Popconfirm,
  Empty,
  Tooltip,
} from 'antd';
import {
  CloudUploadOutlined,
  CloudDownloadOutlined,
  DeleteOutlined,
  RocketOutlined,
  SyncOutlined,
  DatabaseOutlined,
  ExperimentOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

interface TrainedModel {
  id: string;
  name: string;
  model_path: string;
  status: string;
  port?: number;
  device: string;
  created_at: string;
  cloud_path?: string;
  cloud_url?: string;
}

const ModelsPage: React.FC = () => {
  const [models, setModels] = useState<TrainedModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();

  const FINETUNING_API = import.meta.env.VITE_FINETUNING_API_URL || 'http://localhost:5000';

  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${FINETUNING_API}/api/models/`);
      if (response.ok) {
        const data = await response.json();
        setModels(data);
      } else {
        message.error('Failed to load models');
      }
    } catch (error) {
      console.error('Error loading models:', error);
      message.error('Failed to connect to training API');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadToCloud = async (values: any) => {
    setUploading(true);
    try {
      const response = await fetch(`${FINETUNING_API}/api/models/upload-to-cloud`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model_path: values.modelPath,
          model_name: values.modelName,
          base_model: values.baseModel || 'Qwen/Qwen3-8B',
        }),
      });

      if (response.ok) {
        const result = await response.json();
        message.success(`Model uploaded! ${result.files_uploaded.length} files saved to cloud.`);
        setUploadModalVisible(false);
        form.resetFields();
        loadModels();
      } else {
        const error = await response.json();
        message.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      message.error('Failed to upload model to cloud');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (modelId: string) => {
    try {
      const response = await fetch(`${FINETUNING_API}/api/models/${modelId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        message.success('Model deleted');
        loadModels();
      } else {
        message.error('Failed to delete model');
      }
    } catch (error) {
      message.error('Failed to delete model');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: TrainedModel) => (
        <Space direction="vertical" size={0}>
          <strong>{name}</strong>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.model_path}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const colors: Record<string, string> = {
          uploaded: 'green',
          running: 'blue',
          stopped: 'default',
          deploying: 'orange',
          error: 'red',
        };
        return <Tag color={colors[status] || 'default'}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Storage',
      dataIndex: 'device',
      key: 'device',
      width: 100,
      render: (device: string) => (
        <Tag icon={device === 'cloud' ? <CloudUploadOutlined /> : <DatabaseOutlined />}>
          {device}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => (
        <Space>
          <ClockCircleOutlined />
          {new Date(date).toLocaleString()}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: any, record: TrainedModel) => (
        <Space>
          {record.status === 'uploaded' && (
            <Tooltip title="Download Model">
              <Button
                type="text"
                icon={<CloudDownloadOutlined />}
                onClick={() => {
                  if (record.cloud_url) {
                    window.open(record.cloud_url, '_blank');
                  } else {
                    message.info('No download URL available');
                  }
                }}
              />
            </Tooltip>
          )}
          {record.status === 'stopped' && (
            <Tooltip title="Deploy Model">
              <Button type="text" icon={<RocketOutlined />} />
            </Tooltip>
          )}
          <Popconfirm
            title="Are you sure you want to delete this model?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const cloudModels = models.filter((m) => m.status === 'uploaded');
  const localModels = models.filter((m) => m.status !== 'uploaded');

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <ExperimentOutlined /> Trained Models
          </Title>
          <Text type="secondary">Manage your fine-tuned models and cloud storage</Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<SyncOutlined />} onClick={loadModels} loading={loading}>
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              onClick={() => setUploadModalVisible(true)}
            >
              Upload to Cloud
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Models"
              value={models.length}
              prefix={<ExperimentOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Cloud Storage"
              value={cloudModels.length}
              prefix={<CloudUploadOutlined />}
              suffix="models"
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Local Models"
              value={localModels.length}
              prefix={<DatabaseOutlined />}
              suffix="models"
            />
          </Card>
        </Col>
      </Row>

      {/* Models Table */}
      <Card title="All Models">
        {models.length === 0 ? (
          <Empty
            description="No trained models yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" onClick={() => setUploadModalVisible(true)}>
              Upload First Model
            </Button>
          </Empty>
        ) : (
          <Table
            columns={columns}
            dataSource={models}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        )}
      </Card>

      {/* Upload Modal */}
      <Modal
        title="Upload Model to Cloud Storage"
        open={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleUploadToCloud}>
          <Form.Item
            name="modelPath"
            label="Model Path"
            rules={[{ required: true, message: 'Please enter the model path' }]}
          >
            <Input placeholder="/workspace/Qwen3-8B-Maximo-SQL" />
          </Form.Item>
          <Form.Item
            name="modelName"
            label="Model Name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="Qwen3-8B-Maximo-SQL-v1" />
          </Form.Item>
          <Form.Item name="baseModel" label="Base Model">
            <Input placeholder="Qwen/Qwen3-8B" defaultValue="Qwen/Qwen3-8B" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setUploadModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={uploading}>
                Upload to Cloud
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ModelsPage;
